import { useState, useEffect } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store/hooks';
import { RootState } from '../store/store';
import {
  loadFieldGroups,
  addFieldGroup,
  editFieldGroup,
  removeFieldGroup,
  moveFieldGroup,
} from '../features/surveys/surveyFieldGroupsSlice';
import { SurveyFieldGroup } from '../features/surveys/types';
import { SurveyFieldGroupEditor } from './SurveyFieldGroupEditor';
import { Icon } from '@iconify/react';

interface Props {
  surveyId: string;
}

export function SurveyFieldGroupsManager({ surveyId }: Props) {
  const { confirm } = useConfirmDialog();
  const dispatch = useAppDispatch();
  const { items: groups, loading, error } = useSelector((state: RootState) => state.surveyFieldGroups);
  const [isAdding, setIsAdding] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SurveyFieldGroup | null>(null);

  useEffect(() => {
    if (surveyId) {
      dispatch(loadFieldGroups({ surveyId }));
    }
  }, [dispatch, surveyId]);

  const totalWeight = groups.reduce((sum, g) => sum + g.weight, 0);
  const isWeightValid = Math.abs(totalWeight - 100) < 0.01;

  const handleAdd = async (data: { name: string; weight: number; threshold?: number | null }) => {
    await dispatch(addFieldGroup({ surveyId, payload: data }));
    setIsAdding(false);
  };

  const handleEdit = async (data: { name: string; weight: number; threshold?: number | null }) => {
    if (editingGroup) {
      await dispatch(
        editFieldGroup({
          surveyId,
          groupId: editingGroup.id,
          payload: data,
        })
      );
      setEditingGroup(null);
    }
  };

  const handleDelete = async (groupId: string) => {
    const ok = await confirm({
      title: 'Eliminar agrupación',
      message: '¿Estás seguro de que quieres eliminar esta agrupación?',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    await dispatch(removeFieldGroup({ surveyId, groupId }));
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newGroups = [...groups];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newGroups.length) {
      const temp = newGroups[index];
      newGroups[index] = newGroups[targetIndex];
      newGroups[targetIndex] = temp;
      
      const reorderPayload = newGroups.map((g, i) => ({
        id: g.id,
        order: i,
      }));
      
      await dispatch(moveFieldGroup({ surveyId, groups: reorderPayload }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">Agrupaciones y Ponderaciones</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Define las áreas de la encuesta y su peso sobre el puntaje final.
          </p>
        </div>
        {!isAdding && !editingGroup && (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Icon icon="mdi:plus" className="w-5 h-5" />
            Nueva Agrupación
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-md bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      {isAdding && (
        <SurveyFieldGroupEditor
          onSubmit={handleAdd}
          onCancel={() => setIsAdding(false)}
          loading={loading}
        />
      )}

      {editingGroup && (
        <SurveyFieldGroupEditor
          group={editingGroup}
          onSubmit={handleEdit}
          onCancel={() => setEditingGroup(null)}
          loading={loading}
        />
      )}

      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted text-muted-foreground font-medium border-b border-border">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3 w-32">Ponderación</th>
              <th className="px-4 py-3 w-32 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {groups.map((group, index) => (
              <tr key={group.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-4 py-3 font-medium">{group.name}</td>
                <td className="px-4 py-3">{group.weight}%</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => handleMove(index, 'up')}
                      disabled={index === 0}
                      className="p-1.5 rounded-md hover:bg-background disabled:opacity-30 transition-colors"
                      title="Mover arriba"
                    >
                      <Icon icon="mdi:chevron-up" className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleMove(index, 'down')}
                      disabled={index === groups.length - 1}
                      className="p-1.5 rounded-md hover:bg-background disabled:opacity-30 transition-colors"
                      title="Mover abajo"
                    >
                      <Icon icon="mdi:chevron-down" className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setEditingGroup(group)}
                      className="p-1.5 rounded-md hover:bg-background text-primary transition-colors"
                      title="Editar"
                    >
                      <Icon icon="mdi:pencil" className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="p-1.5 rounded-md hover:bg-background text-destructive transition-colors"
                      title="Eliminar"
                    >
                      <Icon icon="mdi:delete" className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {groups.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                  No hay agrupaciones definidas para esta encuesta.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-muted/30 font-bold border-t border-border">
            <tr>
              <td className="px-4 py-3 text-right">Total:</td>
              <td className={`px-4 py-3 ${isWeightValid ? 'text-green-600' : 'text-destructive'}`}>
                {totalWeight}%
              </td>
              <td className="px-4 py-3 text-right text-xs font-normal">
                {!isWeightValid && (
                  <span className="text-destructive">La suma debe ser 100%</span>
                )}
                {isWeightValid && (
                  <span className="text-green-600">Ponderación válida</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
